import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSelf, logout, updateSelf } from "@/api/auth";
import { api } from "@/api/client";
import { resetSimAccount } from "@/api/sim";
import { useSubscription } from "@/hooks/useSubscription";
import PaywallModal from "@/components/feature/PaywallModal";

function maskPhone(phone: string): string {
  if (!phone) return "未绑定";
  return phone.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-sm rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-foreground-950">{title}</h2>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-foreground-500 text-sm" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AccountSettings() {
  const navigate = useNavigate();
  const sub = useSubscription();
  const [user, setUser] = useState<any>(null);
  const [dispName, setDispName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [smsNotify, setSmsNotify] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [resettingSim, setResettingSim] = useState(false);

  const [activeModal, setActiveModal] = useState<string | null>(null);

  useEffect(() => {
    getSelf().then((u) => {
      setUser(u);
      setDispName(u.displayName || u.username || "");
      if (typeof u.smsNotifyEnabled === 'boolean') setSmsNotify(u.smsNotifyEnabled);
    }).catch(() => {});
  }, []);

  const handleSaveName = async () => {
    if (!dispName.trim()) return;
    setSavingName(true);
    try {
      await updateSelf({ displayName: dispName.trim() });
      setUser((prev: any) => ({ ...prev, displayName: dispName.trim() }));
      setActiveModal(null);
    } catch (e: any) { alert(e.message || "保存失败"); }
    finally { setSavingName(false); }
  };

  const handleSavePassword = async () => {
    if (newPwd.length < 8) { alert("密码至少 8 个字符"); return; }
    if (newPwd !== confirmPwd) { alert("两次密码不一致"); return; }
    setSavingPwd(true);
    try {
      await updateSelf({ password: newPwd, oldPassword: oldPwd });
      alert("密码修改成功");
      setActiveModal(null);
      setOldPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e: any) { alert(e.message || "修改失败"); }
    finally { setSavingPwd(false); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const handleSwitchAccount = () => { logout(); navigate("/login"); };
  const handleResetSim = async () => {
    setResettingSim(true);
    try {
      await resetSimAccount();
      setActiveModal(null);
      alert("模拟盘已重置");
    } catch (error: any) {
      alert(error?.message || "重置失败，请稍后重试");
    } finally {
      setResettingSim(false);
    }
  };

  return (
    <div className="flex-1">
      {/* ═══ 顶部导航 ═══ */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <button onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer shrink-0">
          <i className="ri-arrow-left-s-line text-foreground-600 text-sm" />
        </button>
        <h1 className="text-base font-bold text-foreground-950">账户设置</h1>
      </div>

      {/* ═══ 个人信息 ═══ */}
      <div className="bg-white border-b border-background-100">
        <div className="px-4 py-2 text-[10px] text-foreground-400 font-medium uppercase tracking-wider">个人信息</div>

        <button onClick={() => { setDispName(user?.displayName || user?.username || ""); setActiveModal("name"); }}
          className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 active:bg-background-100 transition-colors">
          <span className="text-sm text-foreground-700 flex-1 text-left">显示名称</span>
          <span className="text-xs text-foreground-400">{user?.displayName || user?.username || "--"}</span>
          <i className="ri-arrow-right-s-line text-foreground-300" />
        </button>

        <div className="mx-4 h-[1px] bg-background-50" />

        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-sm text-foreground-700 flex-1">手机号</span>
          <span className="text-xs text-foreground-400">{maskPhone(user?.phone)}</span>
        </div>

        <div className="mx-4 h-[1px] bg-background-50" />

        <button onClick={() => { setOldPwd(""); setNewPwd(""); setConfirmPwd(""); setActiveModal("password"); }}
          className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 active:bg-background-100 transition-colors">
          <span className="text-sm text-foreground-700 flex-1 text-left">修改密码</span>
          <i className="ri-arrow-right-s-line text-foreground-300" />
        </button>
      </div>

      {/* ═══ 通知（暂时隐藏） ═══ */}
      {/*
      <div className="bg-white border-b border-background-100 mt-2">
        <div className="px-4 py-2 text-[10px] text-foreground-400 font-medium uppercase tracking-wider">通知</div>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-sm text-foreground-700 flex-1">AI 交易短信通知</span>
          <p className="text-[10px] text-foreground-400 mr-2">每笔决策后提醒</p>
          <div onClick={async () => {
              if (!sub.subscribed) { setShowPaywall(true); return; }
              const newVal = !smsNotify;
              setSmsNotify(newVal);
              try { await api.patch("/user/sms-notify", { enabled: newVal }); }
              catch { setSmsNotify(!newVal); }
            }}
            className={`relative rounded-full cursor-pointer transition-colors shrink-0 ${!sub.subscribed ? "opacity-40" : ""} ${smsNotify ? "bg-primary-500" : "bg-foreground-200"}`}
            style={{ height: "22px", width: "40px" }}>
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${smsNotify ? "translate-x-[19px]" : ""}`}
              style={{ width: "16px", height: "16px" }} />
          </div>
        </div>
      </div>
      */}

      {/* ═══ 账号操作 ═══ */}
      <div className="bg-white border-b border-background-100 mt-2">
        <div className="px-4 py-2 text-[10px] text-foreground-400 font-medium uppercase tracking-wider">账号</div>

        <button onClick={handleSwitchAccount}
          className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 active:bg-background-100 transition-colors">
          <span className="text-sm text-foreground-700">切换账号</span>
        </button>

        <div className="mx-4 h-[1px] bg-background-50" />

        <button onClick={() => setActiveModal("reset-sim")}
          className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-amber-50 active:bg-amber-100 transition-colors">
          <span className="text-sm text-amber-600 flex-1 text-left">重置模拟盘</span>
          <span className="text-[10px] text-foreground-400">清空资金、持仓和订单</span>
          <i className="ri-arrow-right-s-line text-foreground-300" />
        </button>

        <div className="mx-4 h-[1px] bg-background-50" />

        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-red-50 active:bg-red-100 transition-colors">
          <span className="text-sm text-red-500 font-medium">退出登录</span>
        </button>
      </div>

      {/* ═══ 弹窗：修改名称 ═══ */}
      {activeModal === "name" && (
        <Modal title="修改显示名称" onClose={() => setActiveModal(null)}>
          <p className="text-xs text-foreground-400 mb-3">其他用户将看到此名称</p>
          <input value={dispName} onChange={(e) => setDispName(e.target.value)}
            className="w-full bg-background-50 rounded-xl px-4 py-3 text-sm text-foreground-950 outline-none border-none mb-4"
            placeholder="输入显示名称" maxLength={20} />
          <div className="flex gap-2">
            <button onClick={() => setActiveModal(null)}
              className="flex-1 py-3 rounded-xl text-sm font-medium bg-background-100 text-foreground-600 cursor-pointer">取消</button>
            <button onClick={handleSaveName} disabled={savingName || !dispName.trim()}
              className="flex-1 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-white cursor-pointer disabled:opacity-50">
              {savingName ? "保存中..." : "保存"}
            </button>
          </div>
        </Modal>
      )}

      {/* ═══ 弹窗：修改密码 ═══ */}
      {activeModal === "password" && (
        <Modal title="修改密码" onClose={() => setActiveModal(null)}>
          <div className="space-y-3 mb-4">
            <input type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)}
              className="w-full bg-background-50 rounded-xl px-4 py-3 text-sm text-foreground-950 outline-none border-none" placeholder="当前密码" />
            <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
              className="w-full bg-background-50 rounded-xl px-4 py-3 text-sm text-foreground-950 outline-none border-none" placeholder="新密码（至少 8 位）" />
            <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
              className="w-full bg-background-50 rounded-xl px-4 py-3 text-sm text-foreground-950 outline-none border-none" placeholder="确认新密码" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setActiveModal(null)}
              className="flex-1 py-3 rounded-xl text-sm font-medium bg-background-100 text-foreground-600 cursor-pointer">取消</button>
            <button onClick={handleSavePassword} disabled={savingPwd || !oldPwd || !newPwd || !confirmPwd}
              className="flex-1 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-white cursor-pointer disabled:opacity-50">
              {savingPwd ? "修改中..." : "确认修改"}
            </button>
          </div>
        </Modal>
      )}

      {/* ═══ 弹窗：重置模拟盘 ═══ */}
      {activeModal === "reset-sim" && (
        <Modal title="确认重置模拟盘" onClose={() => { if (!resettingSim) setActiveModal(null); }}>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 mb-4">
            <div className="flex items-start gap-2">
              <i className="ri-alert-line text-amber-600 text-base mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">重置后无法恢复</p>
                <p className="text-xs text-amber-700 leading-relaxed mt-1">
                  当前模拟资金、全部持仓和历史订单会被清空，并重新建立初始模拟账户。真实账号和会员权益不受影响。
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setActiveModal(null)} disabled={resettingSim}
              className="flex-1 py-3 rounded-xl text-sm font-medium bg-background-100 text-foreground-600 cursor-pointer disabled:opacity-50">
              取消
            </button>
            <button onClick={handleResetSim} disabled={resettingSim}
              className="flex-1 py-3 rounded-xl text-sm font-semibold bg-red-500 text-white cursor-pointer disabled:opacity-50">
              {resettingSim ? "重置中..." : "确认重置"}
            </button>
          </div>
        </Modal>
      )}
      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} description="短信通知功能需要联系客服开通" />
    </div>
  );
}
